import React from 'react';
import { css } from '@emotion/react';
import { DatePicker } from 'components/DatePicker';
import { format } from 'date-fns';

import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { routes } from 'routes';

import { Button } from 'components/Button';
import { FormControl } from 'components/FormControl';
import { FormFooter } from 'components/FormFooter';
import { FormLine } from 'components/FormLine';
import { Input } from 'components/Input';
import { Select } from 'components/Select';
import { useMutation, useQuery } from 'urql';
import { QUERY_BOOKER_ARTISTS, MUTATION_UPSERT_REQUEST } from 'queries/queries-booker';
import { useStore } from 'hooks/useStore';
import { theme } from 'styles/theme';
import { InputPrice } from 'components/InputPrice';
import { SpinnerBlock } from 'components/SpinnerBlock';
import { PriceSummary } from 'screens/booking/components/PriceSummay';
import { FEE_TYPE } from 'screens/booking/steps/FeeStep';
import { FormSectionHeading } from 'components/FormSectionHeading';
import { Radios } from 'components/Radios';
import { Textarea } from 'components/Textarea';
import { emailValidation, nonEmptyValidation } from 'screens/booking/forms/validations';
import { IconEmail } from 'icons/icon-email';
import { IconCalendar } from 'icons/icon-calendar';

enum FORM_REQUEST {
	FEE_TYPE = 'fee_type',

	FEE_AMOUNT = 'fee_amount',
	FEE_TRAVEL = 'fee_travel',

	FEE_OTHER = 'fee_other',

	EVENT_DATE = 'event_date',
	EVENT_CITY = 'event_address_city',
	ARTIST = 'artist_id',
	EVENT_PLACE = 'event_address_place',

	MAIN_EMAIL = 'main_email',
	// EMAIL = 'email',
	// PHONE = 'phone',
}

export const transformToSelectOptions = (data: any, labelKey: string) => {
	if (!data) {
		return;
	}

	// console.log('transform data', data);

	return data.map((item: any) => {
		return { value: item.id, label: item[labelKey] };
	});
};

export const RequestForm = ({ defaultValues, id }: { defaultValues?: any; id?: any }) => {
	const [artists] = useQuery({ query: QUERY_BOOKER_ARTISTS });

	const { notifications } = useStore();

	// nacist interprety, ktere pak priradime do selectu

	const navigate = useNavigate();

	const localDefaultValues = {
		fee_type: defaultValues?.fee_type ? defaultValues?.fee_type : FEE_TYPE.FIXED,

		fee_amount: defaultValues?.fee_amount,
		fee_travel: defaultValues?.fee_travel,

		fee_other: defaultValues?.fee_other,

		event_address_place: defaultValues?.event_address_place,
		event_address_city: defaultValues?.event_address_city,
		event_date: defaultValues?.event_date,

		artist_id: defaultValues?.artist?.id,

		main_email: defaultValues?.main_email,
		// email: defaultValues?.email,
		// phone: defaultValues?.phone,
	};

	const {
		handleSubmit,
		register,
		control,
		formState: { errors, isDirty },
		watch,
	} = useForm({ defaultValues: localDefaultValues });

	const [upsertRequest, executeUpsertRequest] = useMutation(MUTATION_UPSERT_REQUEST);

	const onSubmit = (data: any) => {
		console.log('SUBMIT');
		console.log(data);

		executeUpsertRequest({
			id: id ? id : null,
			input: data,
		}).then((result) => {
			console.log(result.data?.upsertRequest);

			if (result?.error) {
				// VYHODIT ERROR
				return false;
			} else {
				if (!id) {
					navigate(routes.booker.request + result.data?.upsertRequest?.id + '/');
					notifications.addNotification({ title: 'Hotovo', message: 'Poptávka přidána.' });
				} else {
					// THROW NOTIFIKATION SAVED
					notifications.addNotification({ title: 'Hotovo', message: 'Změny uloženy.' });
				}
			}

			// redirect to detail with link
		});
	};

	if (artists.fetching) return <SpinnerBlock />;

	const artistsOptions = transformToSelectOptions(artists?.data?.bookerArtists, 'name');
	const currentArtistOption = watch('artist_id');

	const currentArtist = artists?.data?.bookerArtists.find((item) => {
		return item.id === currentArtistOption;
	});

	const feeAmount = watch(FORM_REQUEST.FEE_AMOUNT) ?? 0;
	const feeTravel = watch(FORM_REQUEST.FEE_TRAVEL) ?? 0;

	const feeType = watch(FORM_REQUEST.FEE_TYPE);

	const priceTotal = feeAmount + feeTravel;

	const feeTypeOptions = [
		{
			label: 'Fixní',
			value: FEE_TYPE.FIXED,
		},
		{ label: 'Jiné podmínky', value: FEE_TYPE.OTHER },
	];

	const isEditDirty = id && isDirty;

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			{/* {JSON.stringify(localDefaultValues)}
			{JSON.stringify(artistsOptions)} */}
			{/* ARTIST ID: {currentArtistOption}
			{JSON.stringify(currentArtist)} */}
			<FormSectionHeading first>Akce</FormSectionHeading>
			<FormLine first>
				<FormControl
					required
					label="Interpret"
					htmlFor={FORM_REQUEST.ARTIST}
					labelWidth={160}
					error={errors[FORM_REQUEST.ARTIST]}
				>
					<Controller
						name={FORM_REQUEST.ARTIST}
						control={control}
						rules={{ required: 'Vyberte interpreta.' }}
						render={({ field }) => {
							return (
								<Select
									{...field}
									options={artistsOptions}
									placeholder="Vyberte interpreta..."
									error={errors[FORM_REQUEST.ARTIST]}
								/>
							);
						}}
					/>
				</FormControl>
			</FormLine>
			<FormLine>
				<FormControl
					required
					label="Datum"
					htmlFor={FORM_REQUEST.EVENT_DATE}
					labelWidth={160}
					error={errors[FORM_REQUEST.EVENT_DATE]}
				>
					<Controller
						name={FORM_REQUEST.EVENT_DATE}
						control={control}
						rules={{ required: 'Vyberte datum.' }}
						render={({ field }) => {
							const { onChange } = field;

							const changeWithToggle = (date: Date, toggle: () => void) => {
								const dateValue = format(date, 'YYY-MM-dd');

								onChange(dateValue);
								toggle();
							};

							return (
								<DatePicker
									renderTrigger={({ isOpen }) => (
										<div
											css={css`
												height: 44px;
												border: 1px solid #d4d5de;
												border-radius: 8px;
												padding: 12px;
												font-size: 14px;
												line-height: 18px;

												padding-left: 44px;
												padding-right: 20px;

												&:hover {
													background: ${theme.color.primary['50']};
													cursor: pointer;
												}

												${isOpen &&
												css`
													outline: none;
													box-shadow: 0 0 0 2px ${theme.color.primary['400']};
													border-color: transparent;
												`}

												${errors[FORM_REQUEST.EVENT_DATE] &&
												css`
													border-color: #ff6767;
												`}

												${!field.value &&
												css`
													color: #8c8fa6;
												`}
											`}
										>
											<div
												css={css`
													position: absolute;
													top: 12px;
													left: 12px;
												`}
											>
												<IconCalendar />
											</div>

											{field.value
												? format(new Date(field.value), 'dd.MM.YYY')
												: 'Vyberte datum...'}
										</div>
									)}
									{...field}
									// value={field.value}
									onChange={changeWithToggle}
								/>
							);
						}}
					/>
				</FormControl>
			</FormLine>
			<FormLine>
				<FormControl
					required
					label="Místo"
					htmlFor={FORM_REQUEST.EVENT_PLACE}
					labelWidth={160}
					error={errors[FORM_REQUEST.EVENT_PLACE]}
				>
					<Input
						width={240}
						placeholder="Zadejte místo konání akce..."
						{...register(
							FORM_REQUEST.EVENT_PLACE,
							nonEmptyValidation({ required: 'Zadejte místo konání akce.' }),
						)}
						error={!!errors[FORM_REQUEST.EVENT_PLACE]}
					/>
				</FormControl>
			</FormLine>
			<FormLine>
				<FormControl
					required
					label="Město"
					htmlFor={FORM_REQUEST.EVENT_CITY}
					labelWidth={160}
					error={errors[FORM_REQUEST.EVENT_CITY]}
				>
					<Input
						width={240}
						placeholder="Zadejte město konání akce..."
						{...register(
							FORM_REQUEST.EVENT_CITY,
							nonEmptyValidation({ required: 'Zadejte město konání akce.' }),
						)}
						error={!!errors[FORM_REQUEST.EVENT_CITY]}
					/>
				</FormControl>
			</FormLine>
			<FormSectionHeading>Honorář</FormSectionHeading>
			<FormLine first>
				<FormControl
					label="Typ"
					htmlFor={FORM_REQUEST.FEE_TYPE}
					labelWidth={160}
					error={errors[FORM_REQUEST.FEE_TYPE]}
					spaceMedium
				>
					<Controller
						name={FORM_REQUEST.FEE_TYPE}
						control={control}
						defaultValue={feeTypeOptions[0].value}
						render={({ field }) => {
							return <Radios options={feeTypeOptions} {...field} />;
						}}
					/>
				</FormControl>
			</FormLine>
			{feeType === FEE_TYPE.FIXED && (
				<>
					<FormLine>
						<FormControl
							required
							label="Honorář"
							htmlFor={FORM_REQUEST.FEE_AMOUNT}
							labelWidth={160}
							error={errors[FORM_REQUEST.FEE_AMOUNT]}
						>
							<Controller
								name={FORM_REQUEST.FEE_AMOUNT}
								control={control}
								rules={{
									required: 'Zadejte částku v CZK.',
									max: { value: 10000000, message: 'Maximální částka je 10 000 000 CZK' },
								}}
								render={({ field }) => (
									<InputPrice
										placeholder="0"
										width={164}
										value={field.value}
										onChange={field.onChange}
										error={!!errors[FORM_REQUEST.FEE_AMOUNT]}
										isVAT={currentArtist?.vat_invoicing}
										// showVATCalculation
									/>
								)}
							/>
							{/* <Input
						width={164}
						placeholder="0"
						{...register(FORM_REQUEST.FEE_AMOUNT, {
							required: 'Zadejte částku v CZK.',
							valueAsNumber: true,
						})}
						error={!!errors[FORM_REQUEST.FEE_AMOUNT]}
						isNumber
					/> */}
						</FormControl>
					</FormLine>
					<FormLine>
						<FormControl
							required
							label="Cestovné"
							htmlFor={FORM_REQUEST.FEE_TRAVEL}
							labelWidth={160}
							error={errors[FORM_REQUEST.FEE_TRAVEL]}
						>
							<Controller
								name={FORM_REQUEST.FEE_TRAVEL}
								control={control}
								rules={{
									required: 'Zadejte částku v CZK.',
									min: { value: 0, message: 'Zadejte částku v CZK' },
									max: { value: 10000000, message: 'Maximální částka je 10 000 000 CZK' },
								}}
								render={({ field }) => (
									<InputPrice
										placeholder="0"
										width={164}
										value={field.value}
										onChange={field.onChange}
										error={!!errors[FORM_REQUEST.FEE_TRAVEL]}
										isVAT={currentArtist?.vat_invoicing}
									/>
								)}
							/>
						</FormControl>
					</FormLine>
					<FormLine>
						<FormControl label="Celkem" labelWidth={160}>
							<PriceSummary total={priceTotal} isVat={currentArtist?.vat_invoicing} />
						</FormControl>
					</FormLine>
				</>
			)}
			{feeType === FEE_TYPE.OTHER && (
				<FormLine>
					<FormControl
						required
						label="Popis"
						htmlFor={FORM_REQUEST.FEE_OTHER}
						labelWidth={160}
						error={errors[FORM_REQUEST.FEE_OTHER]}
					>
						<Textarea
							placeholder="Zadejte podmínky..."
							{...register(FORM_REQUEST.FEE_OTHER, { required: 'Zadejte podmínky.' })}
							error={!!errors[FORM_REQUEST.FEE_OTHER]}
						/>
					</FormControl>
				</FormLine>
			)}
			<FormSectionHeading desc="Na tento e-mail bude poslán odkaz na poptávku.">
				Poptávající
			</FormSectionHeading>
			<FormLine>
				<FormControl
					label="E-mail"
					htmlFor={FORM_REQUEST.MAIN_EMAIL}
					labelWidth={160}
					error={errors[FORM_REQUEST.MAIN_EMAIL]}
					required
				>
					<Input
						icon={<IconEmail />}
						{...register(FORM_REQUEST.MAIN_EMAIL, emailValidation({ required: 'Zadejte e-mail.' }))}
						error={!!errors[FORM_REQUEST.MAIN_EMAIL]}
					/>
				</FormControl>
			</FormLine>
			{/* <FormLine>
				<FormControl
					required
					label="E-mail"
					htmlFor={FORM_REQUEST.EMAIL}
					labelWidth={160}
					error={errors[FORM_REQUEST.EMAIL]}
				>
					<Input
						width={164}
						placeholder="@"
						{...register(
							FORM_REQUEST.EMAIL,
							emailValidation({ required: 'Zadejte kontaktni e-mail.' }),
						)}
						error={!!errors[FORM_REQUEST.EMAIL]}
					/>
				</FormControl>
			</FormLine>
			<FormLine>
				<FormControl
					required
					label="Telefon"
					htmlFor={FORM_REQUEST.PHONE}
					labelWidth={160}
					error={errors[FORM_REQUEST.PHONE]}
				>
					<Input
						width={164}
						placeholder=""
						{...register(FORM_REQUEST.PHONE, {
							required: 'Zadejte kontaktni telefon.',
						})}
						error={!!errors[FORM_REQUEST.PHONE]}
					/>
				</FormControl>
			</FormLine> */}

			{isEditDirty && (
				<div
					css={css`
						background: #fffaf0;
						border: 1px solid #ffca6a;
						border-radius: 12px;
						border-radius: 12px;
						padding: 12px 20px;
						color: #b37300;
						font-size: 14px;
						line-height: 20px;
						margin-top: 20px;
					`}
				>
					Některé hodnoty byly změněny. Uložení odešle změny na poptávajícího.
				</div>
			)}

			<FormFooter>
				<Button type="submit" isProcessing={upsertRequest.fetching}>
					{id ? (isEditDirty ? 'Uložit a odeslat změny' : 'Uložit') : 'Přidat a odeslat'}
				</Button>
			</FormFooter>
		</form>
	);
};
